import React, { useEffect, useRef } from 'react';
import { X, Terminal, AlertCircle, Info, Clock, AlertTriangle } from 'lucide-react';

export interface LogEntry {
  type: 'info' | 'error' | 'warn';
  message: string;
  timestamp: number;
}

interface LogViewerProps {
  logs: LogEntry[];
  isOpen: boolean;
  onClose: () => void;
  onClear: () => void;
}

const LogViewer: React.FC<LogViewerProps> = ({ logs, isOpen, onClose, onClear }) => {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center pointer-events-none p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-3xl bg-slate-950 shadow-2xl rounded-xl border border-slate-700 pointer-events-auto flex flex-col h-[600px] max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-slate-800 bg-slate-900 rounded-t-xl">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
            <Terminal className="w-4 h-4 text-cyan-500" />
            System Console
            <span className="bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full text-[10px] border border-slate-700">{logs.length} events</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClear} className="text-xs text-slate-400 hover:text-white px-3 py-1.5 hover:bg-slate-800 rounded transition border border-transparent hover:border-slate-700">Clear</button>
            <button onClick={onClose} className="text-slate-400 hover:text-white p-1.5 hover:bg-slate-800 rounded transition"><X className="w-4 h-4" /></button>
          </div>
        </div>
        
        {/* Log Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2 font-mono text-xs bg-slate-950">
          {logs.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-700">
              <Terminal className="w-12 h-12 mb-2 opacity-20" />
              <p>No logs recorded...</p>
            </div>
          ) : (
            logs.map((log, idx) => (
              <div key={idx} className={`p-2 rounded border-l-2 flex gap-3 animate-in fade-in slide-in-from-bottom-1 duration-150 ${
                log.type === 'error' ? 'bg-red-950/30 border-red-500 text-red-200' :
                log.type === 'warn' ? 'bg-amber-950/30 border-amber-500 text-amber-200' :
                'bg-slate-900/50 border-slate-600 text-slate-300'
              }`}>
                <div className="flex-shrink-0 mt-0.5">
                  {log.type === 'error' ? <AlertCircle className="w-3.5 h-3.5 text-red-500" /> : 
                   log.type === 'warn' ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> : 
                   <Info className="w-3.5 h-3.5 text-slate-500" />}
                </div>
                <div className="flex-1 break-all whitespace-pre-wrap leading-relaxed opacity-90">{log.message}</div>
                <div className="flex-shrink-0 text-slate-600 flex items-center gap-1 min-w-[60px] justify-end" title={new Date(log.timestamp).toLocaleTimeString()}>
                  <Clock className="w-3 h-3" />
                  {new Date(log.timestamp).toLocaleTimeString([], { hour12: false, hour: '2-digit', minute:'2-digit', second:'2-digit' })}
                </div>
              </div>
            ))
          )}
          <div ref={endRef} />
        </div>
      </div>
    </div>
  );
};

export default LogViewer;